import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  X,
  Pencil,
  BarChart3,
  ChevronDown,
  Search,
  MoreVertical,
  Check,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Color Mode Options matching GHL
const COLOR_MODES = [
  { id: 'default', label: 'Default (no color)', preview: 'Stage Name' },
  { id: 'colored_dot', label: 'Colored dot', preview: 'Stage Name', dot: true },
  { id: 'background_tint', label: 'Background tint', preview: 'Stage Name', tint: true },
];

// Preset colors for stages
const STAGE_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', 
  '#84CC16', '#22C55E', '#10B981', '#14B8A6',
  '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
];

const PlatformPipelineEditPage = () => {
  const { pipelineId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [pipeline, setPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [colorMode, setColorMode] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('stages');
  
  // Stage editing
  const [editingStage, setEditingStage] = useState(null);
  const [showAddStage, setShowAddStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#3B82F6');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [stageToDelete, setStageToDelete] = useState(null);

  // Drag and drop state
  const [draggedStage, setDraggedStage] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  useEffect(() => {
    fetchPipeline();
  }, [pipelineId, token]);

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/platform/pipelines/${pipelineId}/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPipeline({
        id: pipelineId,
        name: response.data.pipeline_name,
        stage_color_mode: response.data.stage_color_mode,
      });
      setPipelineName(response.data.pipeline_name);
      setColorMode(response.data.stage_color_mode || 'default');
      setStages(response.data.stages || []);
    } catch (err) {
      console.error('Failed to fetch pipeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update pipeline name and color mode
      await axios.patch(`${API}/platform/pipelines/${pipelineId}`, {
        name: pipelineName,
        stage_color_mode: colorMode,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reorder stages
      const stageIds = stages.map(s => s.id);
      await axios.post(`${API}/platform/pipelines/${pipelineId}/stages/reorder`, {
        stage_ids: stageIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setHasChanges(false);
      setPipeline(prev => ({ ...prev, name: pipelineName, stage_color_mode: colorMode }));
    } catch (err) {
      console.error('Failed to save pipeline:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/platform/pipelines/${pipelineId}/stages`, {
        name: newStageName,
        color: newStageColor,
        show_in_reports: true,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStages([...stages, response.data.stage]);
      setNewStageName('');
      setNewStageColor('#3B82F6');
      setShowAddStage(false);
    } catch (err) {
      console.error('Failed to add stage:', err);
    }
  };

  const handleUpdateStage = async (stageId, updates) => {
    try {
      await axios.patch(`${API}/platform/pipeline-stages/${stageId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStages(stages.map(s => s.id === stageId ? { ...s, ...updates } : s));
      setEditingStage(null);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;
    
    try {
      await axios.delete(`${API}/platform/pipeline-stages/${stageToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStages(stages.filter(s => s.id !== stageToDelete));
      setShowDeleteDialog(false);
      setStageToDelete(null);
    } catch (err) {
      console.error('Failed to delete stage:', err);
    }
  };

  // Drag handlers
  const handleDragStart = (e, stage) => {
    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    if (draggedStage && draggedStage.id !== stage.id) {
      setDragOverStage(stage.id);
    }
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (!draggedStage || draggedStage.id === targetStage.id) return;

    const newStages = [...stages];
    const draggedIndex = newStages.findIndex(s => s.id === draggedStage.id);
    const targetIndex = newStages.findIndex(s => s.id === targetStage.id);

    newStages.splice(draggedIndex, 1);
    newStages.splice(targetIndex, 0, draggedStage);

    // Update sort orders
    newStages.forEach((stage, index) => {
      stage.sort_order = index + 1;
    });

    setStages(newStages);
    setDraggedStage(null);
    setDragOverStage(null);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedStage(null);
    setDragOverStage(null);
  };

  const filteredStages = stages.filter(stage => 
    stage.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/platform/opportunities')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          {editingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={pipelineName}
                onChange={(e) => {
                  setPipelineName(e.target.value);
                  setHasChanges(true);
                }}
                className="text-xl font-bold w-64"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>
                <Check className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-stone-900 italic">{pipelineName}</h1>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingName(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/platform/opportunities')}>
            Cancel
          </Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="stages">Stages</TabsTrigger>
          <TabsTrigger value="smart-tags">Smart Tags</TabsTrigger>
        </TabsList>

        <TabsContent value="stages" className="space-y-6">
          {/* Color Mode Selector */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-stone-900">Set pipeline display colors</p>
                  <p className="text-sm text-stone-500">Choose how stage colors appear across your pipeline views</p>
                </div>
                <div className="flex items-center gap-3">
                  {COLOR_MODES.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => {
                        setColorMode(mode.id);
                        setHasChanges(true);
                      }}
                      className={cn(
                        'px-4 py-2 rounded-lg border-2 transition-all',
                        colorMode === mode.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      )}
                      data-testid={`color-mode-${mode.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {mode.dot && (
                          <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                        )}
                        <span className={cn(
                          'text-sm font-medium',
                          mode.tint && 'px-2 py-0.5 bg-blue-100 rounded'
                        )}>
                          {mode.preview}
                        </span>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">{mode.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stages List */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowAddStage(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Stage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-slate-50 rounded-t-lg text-sm font-medium text-stone-600">
                <div className="col-span-1"></div>
                <div className="col-span-5 flex items-center gap-2">
                  <span className="text-xs">A</span>
                  <span>Stage Name</span>
                </div>
                <div className="col-span-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>Show in Reports</span>
                </div>
                <div className="col-span-2"></div>
                <div className="col-span-1">Actions</div>
              </div>

              {/* Stages */}
              <div className="divide-y divide-slate-100">
                {filteredStages.map((stage, index) => (
                  <div
                    key={stage.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, stage)}
                    onDragOver={(e) => handleDragOver(e, stage)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, stage)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      'grid grid-cols-12 gap-4 px-4 py-3 items-center transition-colors',
                      dragOverStage === stage.id && 'bg-blue-50',
                      draggedStage?.id === stage.id && 'opacity-50'
                    )}
                    data-testid={`stage-row-${stage.id}`}
                  >
                    {/* Drag Handle */}
                    <div className="col-span-1 cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-5 w-5 text-stone-400" />
                    </div>
                    
                    {/* Stage Name with Color */}
                    <div className="col-span-5">
                      {editingStage === stage.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={stage.name}
                            onChange={(e) => setStages(stages.map(s => 
                              s.id === stage.id ? { ...s, name: e.target.value } : s
                            ))}
                            className="h-8"
                            autoFocus
                          />
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleUpdateStage(stage.id, { name: stage.name })}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setEditingStage(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {colorMode === 'colored_dot' && stage.color && (
                            <span 
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: stage.color }}
                            />
                          )}
                          <span className={cn(
                            'font-medium text-stone-900',
                            colorMode === 'background_tint' && 'px-2 py-0.5 rounded',
                          )} style={colorMode === 'background_tint' && stage.color ? {
                            backgroundColor: `${stage.color}20`
                          } : {}}>
                            {stage.name}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Show in Reports Toggle */}
                    <div className="col-span-3 flex items-center gap-2">
                      <Switch
                        checked={stage.show_in_reports !== false}
                        onCheckedChange={(checked) => handleUpdateStage(stage.id, { show_in_reports: checked })}
                      />
                      {stage.show_in_reports !== false && (
                        <Badge variant="outline" className="text-xs">
                          {stage.report_category || 'Open'}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Color Picker */}
                    <div className="col-span-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="gap-2">
                            <span 
                              className="w-4 h-4 rounded border border-slate-200" 
                              style={{ backgroundColor: stage.color || '#E5E7EB' }}
                            />
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <div className="grid grid-cols-4 gap-1 p-2">
                            {STAGE_COLORS.map((color) => (
                              <button
                                key={color}
                                className={cn(
                                  'w-6 h-6 rounded border-2 transition-all',
                                  stage.color === color ? 'border-stone-900' : 'border-transparent'
                                )}
                                style={{ backgroundColor: color }}
                                onClick={() => handleUpdateStage(stage.id, { color })}
                              />
                            ))}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingStage(stage.id)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => {
                              setStageToDelete(stage.id);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="smart-tags">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-stone-500">Smart Tags configuration coming in Phase 2</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Stage Dialog */}
      <Dialog open={showAddStage} onOpenChange={setShowAddStage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Stage</DialogTitle>
            <DialogDescription>
              Create a new stage for this pipeline
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Stage Name</Label>
              <Input
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                placeholder="e.g., New Lead, Qualified, Proposal Sent"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="grid grid-cols-8 gap-2">
                {STAGE_COLORS.map((color) => (
                  <button
                    key={color}
                    className={cn(
                      'w-8 h-8 rounded border-2 transition-all',
                      newStageColor === color ? 'border-stone-900 scale-110' : 'border-transparent'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewStageColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStage(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddStage} disabled={!newStageName.trim()}>
              Add Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Stage</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this stage? This action cannot be undone.
              Any opportunities in this stage will need to be moved first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteStage}>
              Delete Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformPipelineEditPage;
